/** Backup tab — every archived session across all three destinations.
 *
 * The three columns are deliberate. A session lives in Postgres, on the
 * VPS disk, and in S3, and those fail for different reasons — collapsing
 * them into one "backed up" flag would hide which one broke, and they are
 * not equally recoverable:
 *
 *   Postgres  the 15:40 capture. UNRECOVERABLE if missed — expiring
 *             contracts delist and the broker window closes.
 *   disk      the 16:10 dump's local copy.
 *   S3        the same dump's off-box copy; the only one that survives
 *             the instance.
 *
 * So a red S3 cell is never data loss — the rows are still in Postgres
 * and the sync button re-sends them. The page says so, because a red row
 * on a page called Backup reads as "your data is gone" otherwise.
 */

import { useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  archiveApi,
  type ArchiveFile,
  type ArchiveSession,
  type SessionState,
  type SyncResult,
} from "@/lib/archiveApi";

const STATE_LABEL: Record<SessionState, [string, string]> = {
  synced: ["synced to S3", "pill bg-success-bg text-success"],
  partial: ["partly synced", "pill bg-danger-bg text-danger"],
  missing: ["not in S3", "pill bg-danger-bg text-danger"],
  // Today before 16:10. Amber, not red: nothing has failed yet.
  pending: ["dump due 16:10", "pill bg-warning-bg text-warning"],
  // The serious one — and the one a retry cannot fix.
  not_captured: ["not captured", "pill bg-danger-bg text-danger"],
};

export function BackupPage(): ReactElement {
  // Today by default (days=0) — the evening question is "did today land?",
  // not a 30-row history. The selector keeps the history reachable.
  const [days, setDays] = useState(0);
  const q = useQuery({
    queryKey: ["archive-sessions", days],
    queryFn: () => archiveApi.sessions(days),
  });

  return (
    <div className="mx-auto max-w-5xl p-4">
      <div className="mb-4 flex items-baseline justify-between">
        <div>
          <h2>Backup</h2>
          <p className="text-sm text-ink-secondary">
            Capture 15:40 → Postgres, dump {q.data?.dump_time_ist ?? "16:10"} → disk + S3
            {q.data?.bucket ? ` (${q.data.bucket})` : ""}
          </p>
        </div>
        <select
          // Every other select in the app carries these explicitly; without
          // them the browser default paints a WHITE box on the dark page.
          className="w-auto rounded-ctl border border-line bg-surface-1 px-2 py-1 text-sm text-ink"
          value={days}
          onChange={(e) => {
            setDays(Number(e.target.value));
          }}
          aria-label="How far back to show"
        >
          <option value={0}>Today</option>
          <option value={10}>Last 10 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      <Body loading={q.isPending} error={q.isError} sessions={q.data?.sessions} />
    </div>
  );
}

function Body({
  loading,
  error,
  sessions,
}: Readonly<{
  loading: boolean;
  error: boolean;
  sessions: ArchiveSession[] | undefined;
}>): ReactElement {
  if (loading) return <p className="text-sm text-ink-secondary">Loading archive…</p>;
  if (error || sessions === undefined)
    return <p className="text-sm text-danger">Couldn&apos;t read the archive.</p>;
  if (sessions.length === 0)
    return <p className="text-sm text-ink-secondary">Nothing captured in this window.</p>;
  return (
    <>
      <Summary sessions={sessions} />
      {sessions.map((s) => (
        <SessionRow key={s.trade_date} session={s} />
      ))}
    </>
  );
}

/** Totals worth seeing without expanding anything: how much is held, and
 * whether any of it has not left the box. */
function Summary({ sessions }: Readonly<{ sessions: ArchiveSession[] }>): ReactElement {
  const captured = sessions.filter((s) => s.bars > 0);
  const bars = captured.reduce((n, s) => n + s.bars, 0);
  const greeks = captured.reduce((n, s) => n + s.greeks, 0);
  const optionBars = captured.reduce(
    (n, s) => n + s.files.filter((f) => f.kind === "options").reduce((m, f) => m + f.bars, 0),
    0,
  );
  const unsynced = sessions.filter((s) => s.can_sync).length;
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
      <Tile label="Sessions" value={String(captured.length)} />
      <Tile label="Bars captured" value={bars.toLocaleString()} />
      <Tile
        label="Greeks solved"
        value={optionBars ? `${((100 * greeks) / optionBars).toFixed(1)}%` : "—"}
      />
      <Tile
        label="Not in S3"
        value={String(unsynced)}
        tone={unsynced > 0 ? "text-danger" : "text-success"}
      />
    </div>
  );
}

function Tile({
  label,
  value,
  tone,
}: Readonly<{ label: string; value: string; tone?: string }>): ReactElement {
  return (
    <div className="rounded-ctl bg-surface-2 p-3">
      <div className="text-xs text-ink-secondary">{label}</div>
      <div className={`text-xl font-medium ${tone ?? ""}`}>{value}</div>
    </div>
  );
}

function SessionRow({ session }: Readonly<{ session: ArchiveSession }>): ReactElement {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<SyncResult | null>(null);
  const qc = useQueryClient();
  const sync = useMutation({
    mutationFn: () => archiveApi.sync(session.trade_date),
    onSuccess: async (r) => {
      setResult(r);
      // Re-read rather than patching locally: the server owns the receipt,
      // so a partial success shows exactly which files landed.
      if (r.ok) await qc.invalidateQueries({ queryKey: ["archive-sessions"] });
    },
    onError: (e: Error) => {
      setResult({
        trade_date: session.trade_date,
        ok: false,
        uploaded: [],
        errors: [e.message],
        message: e.message,
      });
    },
  });
  return (
    <section className="card mb-2">
      <RowHeader
        session={session}
        open={open}
        onToggle={() => {
          setOpen(!open);
        }}
        busy={sync.isPending}
        label={syncLabel(sync.isPending, result)}
        onSync={() => {
          sync.mutate();
        }}
      />
      {result ? <SyncMessage result={result} /> : null}
      {open ? <FileTable session={session} /> : null}
    </section>
  );
}

function RowHeader({
  session,
  open,
  onToggle,
  busy,
  label,
  onSync,
}: Readonly<{
  session: ArchiveSession;
  open: boolean;
  onToggle: () => void;
  busy: boolean;
  label: string;
  onSync: () => void;
}>): ReactElement {
  const [stateLabel, cls] = STATE_LABEL[session.state];
  return (
    <div className="flex items-center gap-3">
      <button
        className="border-0 bg-transparent px-0 text-left text-sm font-medium"
        onClick={onToggle}
        aria-expanded={open}
      >
        <i className={`ti ti-chevron-${open ? "down" : "right"} mr-1 text-ink-muted`} />
        {session.trade_date}
      </button>
      <span className="font-mono text-sm text-ink-secondary">
        {session.bars.toLocaleString()} bars
      </span>
      <span className="text-sm text-ink-muted">
        {session.files.length} file{session.files.length === 1 ? "" : "s"}
      </span>
      <span className={cls}>{stateLabel}</span>
      <span className="ml-auto">
        <button
          disabled={!session.can_sync || busy}
          onClick={onSync}
          className="text-xs"
          title={
            session.can_sync
              ? "Re-send this session's files to S3"
              : "Nothing to sync — every file is already recorded in S3"
          }
        >
          {label}
        </button>
      </span>
    </div>
  );
}

/** What the button says. Named rather than a nested ternary inline: the
 * three states (working, failed-and-retryable, idle) are the whole point
 * of the control. */
function syncLabel(pending: boolean, result: SyncResult | null): string {
  if (pending) return "Syncing…";
  return result && !result.ok ? "Try again" : "Sync to S3";
}

/** The outcome of a retry. On failure it shows the STORE's own words —
 * "AccessDenied (PutObject)" tells the operator it is the key, where
 * "sync failed" would send them looking at the network. */
function SyncMessage({ result }: Readonly<{ result: SyncResult }>): ReactElement {
  if (result.ok) {
    return (
      <p className="mt-2 text-xs text-success">
        <i className="ti ti-cloud-check mr-1" />
        {result.message}
      </p>
    );
  }
  return (
    <div className="mt-2 bg-danger-bg px-3 py-2 text-xs text-danger">
      <div>{result.message}</div>
      <div className="text-ink-secondary">
        The rows are safe in Postgres — this only re-sends them. Try again later.
      </div>
    </div>
  );
}

function FileTable({ session }: Readonly<{ session: ArchiveSession }>): ReactElement {
  return (
    <table className="mt-3 w-full text-xs">
      <thead className="text-ink-secondary">
        <tr>
          <th className="py-1 text-left font-normal">File</th>
          <th className="text-right font-normal">Bars</th>
          <th className="text-right font-normal">Greeks</th>
          <th className="text-right font-normal">Size</th>
          <th className="text-right font-normal">S3</th>
        </tr>
      </thead>
      <tbody>
        {session.files.map((f) => (
          <FileRow key={f.name} file={f} />
        ))}
      </tbody>
    </table>
  );
}

function FileRow({ file: f }: Readonly<{ file: ArchiveFile }>): ReactElement {
  return (
    <tr className="border-t border-line">
      <td className="py-1 font-mono">
        {/* A plain href: the browser streams the gzip to disk with its own
            progress bar. Served from the VPS disk copy, never S3 — that key
            is PutObject-only and cannot read an object back. */}
        <a
          href={archiveApi.downloadUrl(f)}
          download
          className="text-accent hover:underline"
          title={
            f.on_disk
              ? `Download ${f.name}`
              : "Not on the server's disk — the S3 copy may still exist"
          }
        >
          <i className="ti ti-download mr-1" />
          {f.name}
        </a>
      </td>
      <td className="text-right">{f.bars.toLocaleString()}</td>
      <td className="text-right">
        {f.kind === "options" && f.bars > 0 ? `${((100 * f.greeks) / f.bars).toFixed(1)}%` : "—"}
      </td>
      <td className="text-right">
        {f.size_bytes === null ? "—" : `${(f.size_bytes / 1024).toFixed(0)} KB`}
      </td>
      <td className="text-right">
        {f.in_s3 ? (
          <i className="ti ti-cloud-check text-success" aria-label="in S3" />
        ) : (
          <i className="ti ti-cloud-off text-danger" aria-label="not in S3" />
        )}
      </td>
    </tr>
  );
}
